import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { SettingsPageNav } from "../components/settings/SettingsPageNav";
// Header rendered via Layout
import { useUserPreferencesQuery, useUpdateUserPreferencesMutation } from '../hooks/useUserPreferences';
import { isPushAvailable, requestPushPermission, revokePushToken } from '../lib/firebase';
import { useRegisterPushToken, useUnregisterPushToken } from '../api/push';

const FCM_TOKEN_STORAGE_KEY = 'fcmToken';


function SettingsNotifications() {

    const { data, isLoading, error } = useUserPreferencesQuery();
    const mutation = useUpdateUserPreferencesMutation();

    // Local UI state mirrors server prefs for instant response
    const [formData, setFormData] = useState({
        matchAlerts: true,
        transferAlerts: true,
        leagueAlerts: true
    });

    // Sync local state when query data arrives
    useEffect(() => {
        if (data?.notifications) {
            setFormData(data.notifications);
        }
    }, [data]);

    const changeHandler = (key) => {
        if (mutation.isLoading) return; // Prevent rapid spamming

        const updatedData = {
            ...formData,
            [key]: !formData[key],
        };

        // Immediate UI feedback
        setFormData(updatedData);

        mutation.mutate({ notifications: updatedData }, {
            onError: () => {
                // Rollback UI state handled by hook, but ensure local rollback
                setFormData((prev) => ({ ...prev, [key]: !updatedData[key] }));
            },
        });
    };

    // Browser push (FCM) — separate from email prefs. Section hidden when
    // Firebase env vars are unset or the browser lacks the APIs.
    const pushSupported = isPushAvailable();
    const [pushEnabled, setPushEnabled] = useState(() => {
        try { return !!localStorage.getItem(FCM_TOKEN_STORAGE_KEY); } catch (_) { return false; }
    });
    const [pushBusy, setPushBusy] = useState(false);
    const registerPush = useRegisterPushToken();
    const unregisterPush = useUnregisterPushToken();

    const handlePushToggle = async () => {
        if (pushBusy) return;
        setPushBusy(true);
        try {
            if (!pushEnabled) {
                const token = await requestPushPermission();
                if (!token) {
                    toast.error('Browser notifications were not enabled. You can allow them in browser settings.');
                    return;
                }
                await registerPush.mutateAsync(token);
                try { localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token); } catch (_) {}
                setPushEnabled(true);
                toast.success('Browser notifications enabled');
            } else {
                let token = null;
                try { token = localStorage.getItem(FCM_TOKEN_STORAGE_KEY); } catch (_) {}
                if (token) {
                    try { await unregisterPush.mutateAsync(token); } catch (_) { /* non-fatal */ }
                }
                await revokePushToken();
                try { localStorage.removeItem(FCM_TOKEN_STORAGE_KEY); } catch (_) {}
                setPushEnabled(false);
                toast.success('Browser notifications disabled');
            }
        } catch (err) {
            console.error('Push toggle failed:', err);
            toast.error('Could not update browser notification preference.');
        } finally {
            setPushBusy(false);
        }
    };


    return (
        <>
            <div className="page-login page-settings">

                <div className="wrapper">

                    <h1>
                        SETTINGS
                    </h1>

                    <div className="settings-grid">

                        <div className="col">
                            <SettingsPageNav />
                        </div>

                        <div className="col">
                            <div className="form-block switchers-block">
                                <h4>
                                    E-MAIL NOTIFICATIONS
                                </h4>
                                {isLoading ? (
                                    <div className="loading">Loading preferences...</div>
                                ) : error ? (
                                    <div className="error">{error}</div>
                                ) : (
                                    <form>
                                        <div className="switcher">
                                            <input
                                                type="checkbox"
                                                id="notification_match"
                                                name="notification_match"
                                                checked={formData.matchAlerts}
                                                disabled={mutation.isLoading}
                                                onChange={() => changeHandler('matchAlerts')}
                                            />
                                            <label htmlFor="notification_match">
                                                Match Alerts
                                            </label>
                                        </div>
                                        <div className="switcher">
                                            <input
                                                type="checkbox"
                                                id="notification_transfer"
                                                name="notification_transfer"
                                                checked={formData.transferAlerts}
                                                disabled={mutation.isLoading}
                                                onChange={() => changeHandler('transferAlerts')}
                                            />
                                            <label htmlFor="notification_transfer">
                                                Transfer Alerts
                                            </label>
                                        </div>
                                        <div className="switcher">
                                            <input
                                                type="checkbox"
                                                id="notification_league"
                                                name="notification_league"
                                                checked={formData.leagueAlerts}
                                                disabled={mutation.isLoading}
                                                onChange={() => changeHandler('leagueAlerts')}
                                            />
                                            <label htmlFor="notification_league">
                                                League Alerts
                                            </label>
                                        </div>
                                    </form>
                                )}

                            </div>

                            {pushSupported && (
                                <div className="form-block switchers-block">
                                    <h4>
                                        BROWSER NOTIFICATIONS
                                    </h4>
                                    <form>
                                        <div className="switcher">
                                            <input
                                                type="checkbox"
                                                id="notification_browser_push"
                                                name="notification_browser_push"
                                                checked={pushEnabled}
                                                disabled={pushBusy}
                                                onChange={handlePushToggle}
                                            />
                                            <label htmlFor="notification_browser_push">
                                                Enable push notifications in this browser
                                            </label>
                                        </div>
                                    </form>
                                </div>
                            )}

                        </div>

                    </div>


                </div>

            </div>
        </>
    )
}

export {SettingsNotifications}
